import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, FileText, Package, Receipt, Users, UsersRound, Building2,
  UserCircle, ClipboardList, LogOut, Menu, X, ChevronDown, Archive, CalendarDays, Shield
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

const ALL_NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/calendar", icon: CalendarDays, label: "Calendario" },
  { to: "/customers", icon: Users, label: "Clienti" },
  { to: "/quotes", icon: FileText, label: "Preventivi" },
  { to: "/proformas", icon: Receipt, label: "Fatture Proforma", permission: "proformas.create" },
  { to: "/inventories", icon: Package, label: "Inventari" },
  { to: "/operators", icon: UsersRound, label: "Operatori", permission: "users.manage" },
  { to: "/archive", icon: Archive, label: "Archivio", permission: "archive.view" },
  { to: "/audit-log", icon: ClipboardList, label: "Log Attività", permission: "audit.view" },
  { to: "/settings", icon: Building2, label: "Impostazioni", permission: "settings.manage" },
  { to: "/super-admin", icon: Shield, label: "Super Admin", superAdmin: true },
];

export default function Layout() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navItems = ALL_NAV.filter(item => {
    if (item.superAdmin) return user?.is_super_admin;
    if (user?.is_super_admin) return false; // Super admin only sees their panel
    if (item.permission) return hasPermission(item.permission);
    return true;
  });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-60 bg-white/90 backdrop-blur-xl border-r border-zinc-200 fixed inset-y-0 left-0 z-40">
        <div className="flex items-center gap-2 px-5 py-5 border-b border-zinc-200">
          <div className="w-8 h-8 bg-[#002FA7] rounded-sm flex items-center justify-center">
            <Package className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-sm tracking-tight font-['Outfit']" data-testid="app-logo-text">
            Traslochi
          </span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-zinc-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 w-full px-3 py-2 rounded-sm hover:bg-zinc-50 transition-colors text-left"
              >
                <div className="w-7 h-7 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-xs font-medium">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{user?.name}</p>
                  <p className="text-[10px] text-zinc-400 truncate">{user?.role}</p>
                </div>
                <ChevronDown className="w-3 h-3 text-zinc-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-menu-item">
                <UserCircle className="w-4 h-4 mr-2" /> Profilo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                <LogOut className="w-4 h-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-zinc-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-toggle">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <span className="font-semibold text-sm font-['Outfit']">Traslochi</span>
          </div>
          <button onClick={handleLogout} data-testid="mobile-logout-btn" className="text-zinc-500">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
        {sidebarOpen && (
          <nav className="px-4 py-2 space-y-1 border-t border-zinc-100 bg-white">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        )}
      </div>

      {/* Main content */}
      <main className="flex-1 lg:ml-60 pt-14 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
